import type {Control, FieldValues, Path, RegisterOptions} from 'react-hook-form'
import {Controller} from 'react-hook-form'

import style from '@/views/Account/Addresses/AddressForm.module.scss'

interface FormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  placeholder?: string
  type?: string
  isRequired?: boolean
  autoComplete?: string
  rules?: Omit<RegisterOptions<T, Path<T>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>
  id?: string
}

export default function FormField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type = 'text',
  isRequired = false,
  autoComplete,
  rules,
  id,
}: FormFieldProps<T>) {
  const fieldId = id ?? `dialog-${name}`
  const errorId = `${fieldId}-error`

  return (
    <div>
      <label htmlFor={fieldId}>{label}</label>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({
          field: {onChange, onBlur, value, ref},
          fieldState: {invalid, error, isTouched},
        }) => (
          <>
            <input
              id={fieldId}
              type={type}
              ref={ref}
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              required={isRequired}
              aria-required={isRequired ? 'true' : 'false'}
              aria-invalid={invalid ? 'true' : 'false'}
              aria-describedby={error && isTouched ? errorId : undefined}
              autoComplete={autoComplete}
              placeholder={placeholder}
            />
            {error && (
              <div id={errorId} className={style.ErrorMessage} role='alert' aria-live='assertive'>
                {error.message}
              </div>
            )}
          </>
        )}
      />
    </div>
  )
}
